<script setup lang="ts">
	import { cn } from '~/utils/functions'

	export type uiClass = {
		wrapper?: string
		modalBody?: string
		container?: string
		closeBtn?: string
		closeBtnIcon?: string
		modal?: string
		header?: string
		headerContainer?: string
		title?: string
		paragraph?: string
	}

	export type GeneralModalProps = {
		title?: string
		paragraph?: string
		closeOnBg?: boolean
		transitionActiveClass?: string
		transitionLeaveClass?: string
		useCancelBtn?: boolean
		btnClass?: string
		modalClass?: string
		btnPosition?: 'center' | 'right' | 'normal'
		ui?: uiClass
	}

	const cls: uiClass = {
		wrapper: '',
		container: 'flex-col my-auto flex justify-center items-center',
		closeBtn:
			'flex-none bg-white h-[40px] w-[40px] rounded-full flex -translate-y-3 items-center justify-center shadow-md group active:scale-90',
		closeBtnIcon: 'group-hover:stroke-red-700 transition ease-linear',
		modal: 'flex-1 bg-white w-full border-[0.5px] border-gray-200 px-5 md:px-8 lg:px-10 pt-3 pb-8 rounded-lg h-fit',
		header: 'flex items-start justify-between w-full',
		headerContainer: 'tracking-wide flex-1 space-y-3 mb-4',
		title: 'text-blue-950 text-lg md:text-2xl font-semibold mb-0',
		paragraph: 'text-gray-600 text-sm md:text-base font-normal my-0 md:leading-[24px]',
		modalBody: 'modal-body overflow-hidden'
	}

	const {
		transitionActiveClass = 'animate__zoomIn',
		transitionLeaveClass = 'animate__zoomOut',
		useCancelBtn = true,
		btnClass = 'flex',
		modalClass = 'max-w-[526px]'
	} = defineProps<GeneralModalProps>()
	const dialogElement = ref<HTMLElement>()
	const show = defineModel<boolean>('show', { default: false })
	defineOptions({
		inheritAttrs: false
	})
	const emit = defineEmits<{
		change: boolean[]
		close: boolean[]
	}>()

	defineSlots<{
		default: (param: { close: Function }) => void
		title: () => void
	}>()

	const close = () => {
		show.value = false
		emit('change', false)
	}
</script>

<template>
	<teleport to="body">
		<div
			ref="dialogElement"
			class="dialog"
			:class="
				cn(
					{
						'visible z-[1000]': show,
						'pointer-events-none invisible -z-10': !show
					},
					cls.wrapper,
					ui?.wrapper
				)
			"
			role="dialog"
			@click.stop="closeOnBg ? close() : null"
		>
			<transition
				:enter-active-class="`animate__animated animate__faster ${transitionActiveClass}`"
				:leave-active-class="`animate__animated ${transitionLeaveClass}`"
			>
				<div v-if="show" :class="cn(cls.container, ui?.container)">
					<button
						v-if="btnPosition === 'center' && useCancelBtn"
						:class="cn(cls.closeBtn, ui?.closeBtn)"
						type="button"
						data-testid="close-modal-btn"
						@click="close"
					>
						<lazy-tools-icon
							icon="cancel"
							dir="line"
							:height="12.94"
							:width="12.94"
							:path-class="cn(cls.closeBtnIcon, ui?.closeBtnIcon)"
						/>
					</button>

					<div :class="cn(modalClass, cls.modal, ui?.modal)" @click.stop>
						<div :class="cn(cls.header, ui?.header)">
							<div :class="cn(cls.headerContainer, ui?.headerContainer)">
								<h3 :class="cn(cls.title, ui?.title)">
									<slot name="title">
										<span v-html="title || ''" />
									</slot>
								</h3>
								<p :class="cn(cls.paragraph, ui?.paragraph)">
									{{ paragraph || '' }}
								</p>
							</div>

							<div
								v-if="btnPosition !== 'center'"
								class="header float-right clear-end items-end justify-end pt-5"
								:class="btnClass"
							>
								<button
									v-if="useCancelBtn"
									:class="cn(cls.closeBtn, ui?.closeBtn)"
									type="button"
									data-testid="side-close-modal-btn"
									@click="close"
								>
									<lazy-tools-icon
										icon="cancel"
										dir="line"
										:height="12.94"
										:width="12.94"
										:path-class="cn(cls.closeBtnIcon, ui?.closeBtnIcon)"
									/>
								</button>
							</div>
						</div>

						<div :class="cn(cls.modalBody, ui?.modalBody)">
							<slot :close="close" />
						</div>
					</div>
				</div>
			</transition>
		</div>
	</teleport>
</template>

<style scoped>
	.dialog {
		@apply fixed left-0 top-0 z-[1000] flex h-full w-full flex-col overflow-y-auto bg-[#323B4B80] px-3 py-5 backdrop-blur-[2px];
	}
</style>
